import { BaseIdNotRequiredRequest } from "../../../../base/request/BaseIdNotRequiredRequest";
import { toISODate } from "../../../../utils/fechaUtils";
import { MonitoreoBrocaResponse } from "../response/MonitoreoBrocaResponse";

/** Conteos crudos del muestreo de campo (los que ingresa el usuario). */
export type MonitoreoBrocaConteos = {
  arbolesEvaluados: number;
  arbolesBrocados: number;
  frutosEvaluados: number;
  frutosBrocados: number;
  posicion1: number;
  posicion2: number;
  ausentes: number;
  muertas: number;
};

/** Campos derivados del muestreo (los calcula el cliente; el servidor los
 *  almacena tal cual). */
export type MonitoreoBrocaCalculados = {
  arbolesLimpios: number;
  totales: number;
  porcentajePosicion1: number;
  porcentajePosicion2: number;
  porcentajeAusentes: number;
  porcentajeMuertas: number;
  promedioGranosXRama: number;
  infestacionGeneral: number;
  infestacionReal: number;
  mortalidad: number;
};

/** Calcula los campos derivados del muestreo de broca a partir de los conteos de
 *  campo. `infestacionReal` no es `(pos1+pos2)/frutosEvaluados`: es
 *  `infestacionGeneral × porcentajePosicion1 / 100`. `mortalidad` no incluye
 *  `posicion2` en el denominador. Fórmulas tomadas del formulario legacy. */
export function calcularMonitoreoBroca(
  conteos: MonitoreoBrocaConteos,
): MonitoreoBrocaCalculados {
  const {
    arbolesEvaluados,
    arbolesBrocados,
    frutosEvaluados,
    frutosBrocados,
    posicion1,
    posicion2,
    ausentes,
    muertas,
  } = conteos;

  const porcentuar = (superior: number, inferior: number): number =>
    inferior > 0 ? (superior / inferior) * 100 : 0;

  const totales = posicion1 + posicion2 + ausentes + muertas;
  const infestacionGeneral = porcentuar(frutosBrocados, frutosEvaluados);
  const porcentajePosicion1 = porcentuar(posicion1, totales);

  return {
    arbolesLimpios: arbolesEvaluados - arbolesBrocados,
    totales,
    porcentajePosicion1,
    porcentajePosicion2: porcentuar(posicion2, totales),
    porcentajeAusentes: porcentuar(ausentes, totales),
    porcentajeMuertas: porcentuar(muertas, totales),
    promedioGranosXRama:
      arbolesEvaluados > 0 ? frutosEvaluados / arbolesEvaluados : 0,
    infestacionGeneral,
    infestacionReal: (infestacionGeneral * porcentajePosicion1) / 100,
    mortalidad: porcentuar(ausentes + muertas, posicion1 + ausentes + muertas),
  };
}

/** Request de guardado (crear/editar) de un monitoreo de broca. `id` null =
 *  crear; con valor = editar (mismo endpoint `POST /save`). El servidor deriva
 *  `fincaId` desde `loteId`. */
export type MonitoreoBrocaRequest = BaseIdNotRequiredRequest &
  MonitoreoBrocaConteos &
  MonitoreoBrocaCalculados & {
    /** Lote monitoreado. */
    loteId: string;
    /** Fecha del monitoreo (ISO date). */
    fecha: string;
  };

const conteosVacios: MonitoreoBrocaConteos = {
  arbolesEvaluados: 0,
  arbolesBrocados: 0,
  frutosEvaluados: 0,
  frutosBrocados: 0,
  posicion1: 0,
  posicion2: 0,
  ausentes: 0,
  muertas: 0,
};

export const fx_createMonitoreoBrocaRequestEmpty = (): MonitoreoBrocaRequest => {
  return {
    id: null,
    loteId: "",
    fecha: toISODate(new Date()),
    ...conteosVacios,
    ...calcularMonitoreoBroca(conteosVacios),
  };
};

export const fx_createMonitoreoBrocaRequest = (
  response: MonitoreoBrocaResponse,
): MonitoreoBrocaRequest => {
  const conteos: MonitoreoBrocaConteos = {
    arbolesEvaluados: response.arbolesEvaluados,
    arbolesBrocados: response.arbolesBrocados,
    frutosEvaluados: response.frutosEvaluados,
    frutosBrocados: response.frutosBrocados,
    posicion1: response.posicion1,
    posicion2: response.posicion2,
    ausentes: response.ausentes,
    muertas: response.muertas,
  };
  return {
    id: response.id,
    loteId: response.loteId,
    fecha: response.fecha,
    ...conteos,
    ...calcularMonitoreoBroca(conteos),
  };
};
